import PropTypes from 'prop-types';
import {
  alpha,
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ACTION_META,
  LABEL_META,
  convArrows,
  convStrength,
  formatConvictionTooltip,
  formatSignedPercent,
  getMoneyFlowKey,
  getMoneyFlowMeta,
  getPriceChangeColor,
  getSmartMoneyStyle,
  getStrengthTierStyle,
  scoreDot,
  tableColSx,
  tdSx,
  thSx,
} from './shared';

const BehaviorScorePriceCell = ({ score }) => {
  const changeColor = getPriceChangeColor(score.price_change_pct);

  return (
    <Box>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, fontFamily: 'monospace', color: '#1e293b' }}>
        {score.close_price ? score.close_price.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—'}
      </Typography>
      {score.price_change_pct != null && (
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, fontFamily: 'monospace', color: changeColor }}>
          {formatSignedPercent(score.price_change_pct)}
        </Typography>
      )}
    </Box>
  );
};

BehaviorScorePriceCell.propTypes = {
  score: PropTypes.shape({
    close_price: PropTypes.number,
    price_change_pct: PropTypes.number,
  }).isRequired,
};

const BehaviorScoreLabelCell = ({ color, score, t }) => {
  const prevLabelColor = LABEL_META[score.transition?.prev_label]?.color || '#94a3b8';
  let transitionMeta = null;

  if (score.transition?.days_in_label === 1) {
    transitionMeta = (
      <Chip
        label={t('behavior.transition.new')}
        size="small"
        sx={{ height: 16, fontSize: '0.5rem', fontWeight: 800, bgcolor: alpha('#f97316', 0.15), color: '#ea580c', borderRadius: 0.75 }}
      />
    );
  } else if (score.transition?.days_in_label > 1) {
    transitionMeta = (
      <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#94a3b8', fontFamily: 'monospace' }}>
        {score.transition.days_in_label}d
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          label={t(`behavior.labels.${score.behavior_label || 'NEUTRAL'}.short`)}
          size="small"
          sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(color, 0.12), color, borderRadius: 1 }}
        />
        {transitionMeta}
      </Box>
      {score.transition?.prev_label && score.transition.days_in_label === 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.2 }}>
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: prevLabelColor }}>
            {t(`behavior.labels.${score.transition.prev_label}.short`)}
          </Typography>
          <Typography sx={{ fontSize: '0.55rem', color: '#cbd5e1' }}>→</Typography>
        </Box>
      )}
    </Box>
  );
};

BehaviorScoreLabelCell.propTypes = {
  color: PropTypes.string.isRequired,
  score: PropTypes.shape({
    behavior_label: PropTypes.string,
    transition: PropTypes.shape({
      days_in_label: PropTypes.number,
      prev_label: PropTypes.string,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
};

const BehaviorScoreConvictionCell = ({ score }) => {
  const convictionMeta = convStrength(score.conviction);
  const arrows = convArrows(score.transition?.conviction_delta);
  const arrowColor = getPriceChangeColor(score.transition?.conviction_delta || 0);

  return (
    <Tooltip title={formatConvictionTooltip(score.conviction)} arrow placement="top">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'default' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: convictionMeta.color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: convictionMeta.color, whiteSpace: 'nowrap' }}>
          {convictionMeta.label}
        </Typography>
        {arrows && (
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1, color: arrowColor }}>
            {arrows}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

BehaviorScoreConvictionCell.propTypes = {
  score: PropTypes.shape({
    conviction: PropTypes.number,
    transition: PropTypes.shape({ conviction_delta: PropTypes.number }),
  }).isRequired,
};

const BehaviorScoreStrengthCell = ({ t, tier }) => {
  if (!tier || tier === 'REST') {
    return <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>;
  }

  const style = getStrengthTierStyle(tier);

  return (
    <Chip
      label={t(`behavior.tiers.${tier}`)}
      size="small"
      sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: style.bg, color: style.color }}
    />
  );
};

BehaviorScoreStrengthCell.propTypes = {
  t: PropTypes.func.isRequired,
  tier: PropTypes.string,
};

BehaviorScoreStrengthCell.defaultProps = {
  tier: null,
};

const BehaviorScoreSmartMoneyCell = ({ label, t }) => {
  if (!label || ['', 'NEUTRAL'].includes(label)) {
    return <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>;
  }

  const style = getSmartMoneyStyle(label);

  return (
    <Chip
      label={t(`behavior.smLabels.${label}`)}
      size="small"
      sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: style.bg, color: style.color }}
    />
  );
};

BehaviorScoreSmartMoneyCell.propTypes = {
  label: PropTypes.string,
  t: PropTypes.func.isRequired,
};

BehaviorScoreSmartMoneyCell.defaultProps = {
  label: null,
};

const scoreShape = PropTypes.shape({
  behavior_label: PropTypes.string,
  close_price: PropTypes.number,
  company_name: PropTypes.string,
  conviction: PropTypes.number,
  price_change_pct: PropTypes.number,
  scores: PropTypes.shape({
    coil: PropTypes.number,
    distribution: PropTypes.number,
    markup: PropTypes.number,
    support: PropTypes.number,
  }).isRequired,
  sector: PropTypes.shape({
    code: PropTypes.string,
    en: PropTypes.string,
    vi: PropTypes.string,
  }),
  sources: PropTypes.shape({
    money_flow_label: PropTypes.string,
    phase: PropTypes.string,
    smart_money_label: PropTypes.string,
    strength_tier: PropTypes.string,
  }),
  tactical_action: PropTypes.string,
  ticker: PropTypes.string,
  transition: PropTypes.shape({
    conviction_delta: PropTypes.number,
    days_in_label: PropTypes.number,
    prev_label: PropTypes.string,
  }),
});

const BehaviorScoreRow = ({ index, locale, onOpen, score, t }) => {
  const labelMeta = LABEL_META[score.behavior_label] || LABEL_META.NEUTRAL;
  const actionMeta = ACTION_META[score.tactical_action] || ACTION_META.IGNORE;

  return (
    <TableRow
      hover
      onClick={() => onOpen(score.ticker)}
      sx={{
        cursor: 'pointer',
        '&:nth-of-type(even)': { bgcolor: '#fafbfc' },
        '&:hover': { bgcolor: alpha(labelMeta.color, 0.06) },
        transition: 'all 0.15s ease',
      }}
    >
      <TableCell sx={{ ...tdSx, ...tableColSx.index, color: '#94a3b8', fontWeight: 600, fontSize: '0.7rem' }}>{index + 1}</TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.ticker }}>
        <Tooltip title={score.company_name || ''} placement="right" arrow>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.3, color: '#2563eb', whiteSpace: 'nowrap' }}>{score.ticker}</Typography>
            {score.company_name && (
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {score.company_name}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.price }}>
        <BehaviorScorePriceCell score={score} />
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.behavior }}>
        <BehaviorScoreLabelCell color={labelMeta.color} score={score} t={t} />
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.action }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {actionMeta.icon && <Box sx={{ color: actionMeta.color, display: 'flex' }}>{actionMeta.icon}</Box>}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: actionMeta.color }}>{t(`behavior.actions.${score.tactical_action || 'IGNORE'}`)}</Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.conviction }}>
        <BehaviorScoreConvictionCell score={score} />
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.score }}>{scoreDot(score.scores.support, '#f59e0b', t)}</TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.score }}>{scoreDot(score.scores.coil, '#3b82f6', t)}</TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.score }}>{scoreDot(score.scores.markup, '#10b981', t)}</TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.score }}>{scoreDot(score.scores.distribution, '#ef4444', t)}</TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.phase }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{score.sources?.phase ? t(`behavior.phases.${score.sources.phase}`) : '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.strength }}>
        <BehaviorScoreStrengthCell tier={score.sources?.strength_tier} t={t} />
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.smartMoney }}>
        <BehaviorScoreSmartMoneyCell label={score.sources?.smart_money_label} t={t} />
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.moneyFlow }}>
        {score.sources?.money_flow_label ? (
          <Chip
            label={t(`behavior.flowLabels.${getMoneyFlowKey(score.sources.money_flow_label)}`, { defaultValue: score.sources.money_flow_label })}
            size="small"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: getMoneyFlowMeta(score.sources.money_flow_label).bg, color: getMoneyFlowMeta(score.sources.money_flow_label).color }}
          />
        ) : (
          <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>
        )}
      </TableCell>
      <TableCell sx={{ ...tdSx, ...tableColSx.sector }}>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', wordBreak: 'break-word' }}>{score.sector?.[locale] || score.sector?.code || '—'}</Typography>
      </TableCell>
    </TableRow>
  );
};

BehaviorScoreRow.propTypes = {
  index: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
  score: scoreShape.isRequired,
  t: PropTypes.func.isRequired,
};

const BehaviorScoresTable = ({ filtered, locale, onOpen, t }) => (
  <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 1710 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...thSx, ...tableColSx.index }}>{t('behavior.table.index')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.ticker }}>{t('behavior.table.ticker')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.price }}>{t('behavior.table.price')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.behavior }}>{t('behavior.table.behavior')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.action }}>{t('behavior.table.action')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.conviction }}>{t('behavior.table.conviction')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.score }}>{t('behavior.table.support')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.score }}>{t('behavior.table.coil')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.score }}>{t('behavior.table.markup')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.score }}>{t('behavior.table.distribution')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.phase }}>{t('behavior.table.phase')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.strength }}>{t('behavior.table.strength')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.smartMoney }}>{t('behavior.table.smartMoney')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.moneyFlow }}>{t('behavior.table.moneyFlow')}</TableCell>
            <TableCell sx={{ ...thSx, ...tableColSx.sector }}>{t('behavior.table.sector')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={15} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                {t('behavior.noData')}
              </TableCell>
            </TableRow>
          ) : filtered.map((score, index) => (
            <BehaviorScoreRow
              key={score.ticker}
              index={index}
              locale={locale}
              onOpen={onOpen}
              score={score}
              t={t}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

BehaviorScoresTable.propTypes = {
  filtered: PropTypes.arrayOf(scoreShape).isRequired,
  locale: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default BehaviorScoresTable;
